import { useEffect, useRef } from "react";
import { Terminal as XTerm } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import "@xterm/xterm/css/xterm.css";
import { projects } from "../data/projects";

interface Props {
  onRun: () => void;
}

const PROMPT = "\x1b[38;5;208mbrainsteam@labs\x1b[0m:\x1b[38;5;75m~\x1b[0m$ ";

const ABOUT = `
  BrainSteam Labs is a small studio of one (plus a lot of AI).

  We build:
    - tools for ourselves and people we like
    - quietly-good infrastructure
    - the occasional thing that finds an audience

  Some things ship. Some things don't. The ones that do tend to be
  built fast, run cheap, and outlive their original brief.

  Use 'ls projects' to see what's around. 'brainsteam run' for the
  full picture.
`;

const HELP = `
  available commands:
    help               show this list
    ls projects        list current projects and their status
    cat about.md       what is this place
    whoami             a short paragraph about adam
    ask <question>     ask the lab (coming soon)
    contact            open the contact form
    brainsteam run     reveal the project graph
    clear              clear the screen
    date · uptime      the obvious
    version            what version of this is
    joke               roll one out
    (and a few more, if you're curious.)
`;

const WHOAMI_VARIANTS = [
  `
  adam knight — builds web things, has done since the 90s.
  former data scientist, current "vibe coder + architect".
  three kids, one mortgage, an absurd number of side projects.
  base of operations: the south coast of england, mostly before 7am.
`,
  `
  adam knight. twenty-five years of web, counting the bits
  before jQuery was a thing. now mostly typescript + go, a lot
  of AI plumbing, and the occasional ruby app i keep alive out
  of sentiment. writes best code before the kettle boils.
`,
  `
  adam, 40-something, south coast UK. runs to think. codes to
  sleep. three kids, one spouse, one mortgage, and twenty-nine
  things in various states of existence. the point of this page
  is to keep track of them all.
`,
];

const JOKES = [
  "  there are 10 kinds of people. those who understand binary, and the rest of us.",
  "  i would tell you a udp joke but you might not get it.",
  "  why do programmers prefer dark mode? because light attracts bugs.",
  "  a SQL query walks into a bar, sees two tables, asks: \"mind if i join you?\"",
  "  'it works on my machine' is not a test strategy. (adam, approx. weekly.)",
  "  there are only two hard things in computer science: cache invalidation,\n  naming things, and off-by-one errors.",
];

// common commands for did-you-mean
const KNOWN_CMDS = [
  "help",
  "ls",
  "whoami",
  "clear",
  "ask",
  "contact",
  "exit",
  "date",
  "uptime",
  "version",
  "joke",
  "coffee",
  "tea",
  "matrix",
  "vim",
  "emacs",
  "sudo",
  "hello",
];

function distance(a: string, b: string): number {
  if (a === b) return 0;
  const m = a.length;
  const n = b.length;
  if (!m || !n) return m + n;
  const row = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    let prev = i - 1;
    row[0] = i;
    for (let j = 1; j <= n; j++) {
      const old = row[j];
      row[j] =
        a[i - 1] === b[j - 1]
          ? prev
          : 1 + Math.min(prev, row[j], row[j - 1]);
      prev = old;
    }
  }
  return row[n];
}

function didYouMean(cmd: string): string | null {
  if (cmd.length < 2) return null;
  let best: string | null = null;
  let bestDist = Infinity;
  for (const k of KNOWN_CMDS) {
    const d = distance(cmd, k);
    if (d < bestDist) {
      bestDist = d;
      best = k;
    }
  }
  return bestDist <= 2 && best !== cmd ? best : null;
}

const PAGE_LOAD_TIME = Date.now();
function pageUptime(): string {
  const s = Math.round((Date.now() - PAGE_LOAD_TIME) / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}m ${r}s`;
}

function formatDate(): string {
  return new Date().toLocaleString("en-GB", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

function randomMatrixLine(width: number): string {
  const glyphs =
    "アァカサタナハマヤャラワガザダバパイィキシチニヒミリヰギジヂビピウゥクスツヌフムユュルグズブプエェケセテネヘメレヱゲゼデベペオォコソトノホモヨョロヲゴゾドボポヴッン0123456789";
  let s = "";
  for (let i = 0; i < width; i++) {
    s += glyphs[Math.floor(Math.random() * glyphs.length)];
  }
  return s;
}

function colourStatus(status: string): string {
  switch (status) {
    case "live":
      return `\x1b[38;5;46m${status.padEnd(8)}\x1b[0m`;
    case "beta":
      return `\x1b[38;5;220m${status.padEnd(8)}\x1b[0m`;
    case "building":
      return `\x1b[38;5;39m${status.padEnd(8)}\x1b[0m`;
    case "dormant":
      return `\x1b[38;5;244m${status.padEnd(8)}\x1b[0m`;
    default:
      return status.padEnd(8);
  }
}

function listProjects(): string {
  const lines = projects.map((p) => {
    const url = p.url ? `\x1b[38;5;208m${p.url}\x1b[0m` : "\x1b[38;5;244m(unpublished)\x1b[0m";
    return `  ${colourStatus(p.status)}  ${p.name.padEnd(22)} ${url}`;
  });
  return "\n" + lines.join("\n") + "\n";
}

export default function Terminal({ onRun }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const termRef = useRef<XTerm | null>(null);
  const bufferRef = useRef<string>("");
  const onRunRef = useRef(onRun);
  onRunRef.current = onRun;

  useEffect(() => {
    if (!containerRef.current) return;

    const term = new XTerm({
      cursorBlink: true,
      cursorStyle: "block",
      fontFamily:
        '"JetBrains Mono", "Fira Code", "SF Mono", Menlo, Consolas, monospace',
      fontSize: 14,
      lineHeight: 1.4,
      theme: {
        background: "#0a0a0f",
        foreground: "#d1d5db",
        cursor: "#d97757",
        cursorAccent: "#0a0a0f",
        selectionBackground: "rgba(217, 119, 87, 0.4)",
        black: "#0a0a0f",
        brightBlack: "#374151",
        red: "#ef4444",
        brightRed: "#f87171",
        green: "#22c55e",
        brightGreen: "#4ade80",
        yellow: "#f59e0b",
        brightYellow: "#fbbf24",
        blue: "#3b82f6",
        brightBlue: "#60a5fa",
        magenta: "#d946ef",
        brightMagenta: "#e879f9",
        cyan: "#06b6d4",
        brightCyan: "#22d3ee",
        white: "#d1d5db",
        brightWhite: "#f3f4f6",
      },
      allowProposedApi: true,
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(containerRef.current);
    fit.fit();
    termRef.current = term;

    const writeLine = (s: string) => term.write(s.replace(/\n/g, "\r\n"));
    const newPrompt = () => term.write("\r\n" + PROMPT);

    const ORANGE = "\x1b[38;5;208m";
    const DIM = "\x1b[38;5;244m";
    const RESET = "\x1b[0m";
    const RULE = "─".repeat(54);
    writeLine(
      `${ORANGE}   ___           _       __ _                       \r\n  / __\\_ __ __ _(_)_ __ / _\\ |_ ___  __ _ _ __ ___  \r\n /__\\// '__/ _\` | | '_ \\\\ \\| __/ _ \\/ _\` | '_ \` _ \\ \r\n/ \\/  \\ | | (_| | | | | |\\ \\ ||  __/ (_| | | | | | |\r\n\\_____/_|  \\__,_|_|_| |_\\__/\\__\\___|\\__,_|_| |_| |_|\r\n                                          ${DIM}// L A B S${RESET}\r\n`
    );
    writeLine(`${DIM}${RULE}${RESET}\r\n`);
    writeLine(`  a place where things get made.\r\n`);
    writeLine(`${DIM}${RULE}${RESET}\r\n`);
    writeLine(
      `  type ${ORANGE}help${RESET} to look around, or ${ORANGE}brainsteam run${RESET} to reveal the graph.\r\n`
    );
    term.write(PROMPT);

    const handleCommand = (raw: string) => {
      const cmd = raw.trim();
      if (!cmd) return newPrompt();

      // multi-word
      if (cmd === "ls projects") {
        writeLine(listProjects());
        return newPrompt();
      }
      if (cmd === "cat about.md") {
        writeLine(ABOUT);
        return newPrompt();
      }
      if (cmd === "brainsteam run" || cmd === "./brainsteam run") {
        writeLine("\r\n  \x1b[38;5;208minitiating reveal sequence...\x1b[0m\r\n");
        setTimeout(() => onRunRef.current(), 500);
        return;
      }

      const [head, ...rest] = cmd.split(/\s+/);
      switch (head) {
        case "help":
        case "?":
          writeLine(HELP);
          break;
        case "whoami": {
          const v =
            WHOAMI_VARIANTS[Math.floor(Math.random() * WHOAMI_VARIANTS.length)];
          writeLine(v);
          break;
        }
        case "ls":
          writeLine("\r\n  about.md  projects/  contact\r\n");
          break;
        case "clear":
        case "cls":
          term.clear();
          break;
        case "ask":
          if (rest.length === 0) {
            writeLine("\r\n  usage: ask <question>\r\n");
          } else {
            writeLine(
              "\r\n  \x1b[38;5;244mAI mode coming online soon. wire up cerebras + groq in v2.\x1b[0m\r\n"
            );
          }
          break;
        case "contact":
          writeLine(
            "\r\n  \x1b[38;5;244mcontact form unlocks after the reveal. try 'brainsteam run'.\x1b[0m\r\n"
          );
          break;
        case "date":
          writeLine(`\r\n  ${formatDate()}\r\n`);
          break;
        case "uptime":
          writeLine(
            `\r\n  this session: ${pageUptime()}.\r\n  the brand: since roughly 2012, give or take.\r\n`
          );
          break;
        case "version":
        case "--version":
        case "-v":
          writeLine(
            "\r\n  brainsteam labs v0.1.0 — still warm from the oven.\r\n"
          );
          break;
        case "joke": {
          const j = JOKES[Math.floor(Math.random() * JOKES.length)];
          writeLine(`\r\n${j}\r\n`);
          break;
        }
        case "coffee":
        case "tea":
          writeLine(
            `\r\n  \x1b[38;5;220m[pouring ${head}]\x1b[0m ...\r\n  afraid that's all i can manage. i'm a website.\r\n`
          );
          break;
        case "sudo":
          if (rest.length === 0) {
            writeLine("\r\n  sudo what?\r\n");
          } else {
            writeLine(
              `\r\n  \x1b[38;5;220m[sudo]\x1b[0m nice try. we're all root here, and also all the dog.\r\n`
            );
          }
          break;
        case "vim":
        case "vi":
          writeLine(
            "\r\n  you know the drill. :q to escape. hope you saved.\r\n"
          );
          break;
        case ":q":
        case ":q!":
        case ":wq":
          writeLine(
            "\r\n  you were never in vim. but well done, the instinct is strong.\r\n"
          );
          break;
        case "emacs":
          writeLine(
            "\r\n  the thinking person's OS. ctrl-x ctrl-c to go home.\r\n"
          );
          break;
        case "hello":
        case "hi":
        case "hey":
        case "yo":
          writeLine(`\r\n  ${head}. good to have you.\r\n`);
          break;
        case "matrix": {
          writeLine("\r\n");
          const width = Math.min(72, term.cols - 4);
          const rows = 8;
          for (let i = 0; i < rows; i++) {
            setTimeout(() => {
              term.write(
                `  \x1b[38;5;46m${randomMatrixLine(width)}\x1b[0m\r\n`
              );
              if (i === rows - 1) {
                term.write(
                  `\r\n  \x1b[38;5;244mwake up, adam ...\x1b[0m\r\n`
                );
                newPrompt();
              }
            }, i * 90);
          }
          return;
        }
        case "exit":
        case "logout":
        case "quit":
          writeLine(
            "\r\n  there is no exit. only \x1b[38;5;208mbrainsteam run\x1b[0m.\r\n"
          );
          break;
        case "make":
        case "install":
          writeLine(
            "\r\n  nothing to make. the lab makes itself, mostly before breakfast.\r\n"
          );
          break;
        default: {
          const suggestion = didYouMean(head);
          if (suggestion) {
            writeLine(
              `\r\n  command not found: \x1b[38;5;203m${head}\x1b[0m.\r\n  did you mean \x1b[38;5;208m${suggestion}\x1b[0m?\r\n`
            );
          } else {
            writeLine(
              `\r\n  command not found: \x1b[38;5;203m${head}\x1b[0m. try \x1b[38;5;208mhelp\x1b[0m.\r\n`
            );
          }
        }
      }
      newPrompt();
    };

    const disposable = term.onData((data) => {
      const code = data.charCodeAt(0);
      if (code === 13) {
        // enter
        const line = bufferRef.current;
        bufferRef.current = "";
        handleCommand(line);
      } else if (code === 127) {
        // backspace
        if (bufferRef.current.length > 0) {
          bufferRef.current = bufferRef.current.slice(0, -1);
          term.write("\b \b");
        }
      } else if (code < 32) {
        // ignore other control chars (arrow keys etc.)
        return;
      } else {
        bufferRef.current += data;
        term.write(data);
      }
    });

    const onResize = () => fit.fit();
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      disposable.dispose();
      term.dispose();
      termRef.current = null;
    };
  }, []);

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        background: "var(--bg)",
        padding: "1.5rem",
      }}
    >
      <div
        ref={containerRef}
        style={{ width: "100%", height: "100%" }}
      />
    </div>
  );
}
